// The HTTP implementation of ApiClient, and the module the rest of the app imports
// to reach the backend (the mock harness aliases this path wholesale).

import { apiToken, activeAccount, initAuth, signIn } from './auth'
import { currentConfig, loadConfig } from './config'
import { clearSignedOut, isSignedOut } from './signedOut'
import type { Order, OrderForm } from '@glasses/shared'
import type { ApiClient, BootResult, OrderPage, OrderSearch } from './contract'
import { ApiError, type ApiErrorBody } from './errors'

type QueryValue = string | number | undefined | null

function query(params: Record<string, QueryValue>): string {
  const sp = new URLSearchParams()
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '') continue
    sp.set(key, String(value))
  }
  const s = sp.toString()
  return s ? `?${s}` : ''
}

interface RequestOptions {
  method?: string
  body?: unknown
  idempotencyKey?: string
}

/**
 * The API's `{error:{...}}` body when there is one. Anything else - the gateway's
 * authorizer replies `{"message":"Unauthorized"}`, a proxy replies HTML - is built
 * from the status, so the user never sees a bare `http_401`.
 */
async function errorBody(res: Response): Promise<ApiErrorBody> {
  try {
    const json = (await res.json()) as { error?: unknown } | null
    const e = json?.error
    if (typeof e === 'object' && e !== null) return e as ApiErrorBody
  } catch {
    // not JSON
  }
  if (res.status === 401) return { code: 'unauthenticated', message: 'Your session has expired. Sign in again.' }
  if (res.status === 403) return { code: 'forbidden', message: 'You do not have access to this.' }
  return { code: `http_${res.status}`, message: res.statusText || `HTTP ${res.status}` }
}

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const url = `${currentConfig().api_base_url.replace(/\/$/, '')}${path}`
  const headers: Record<string, string> = {
    Authorization: `Bearer ${await apiToken()}`,
    Accept: 'application/json',
  }
  if (options.idempotencyKey) headers['Idempotency-Key'] = options.idempotencyKey
  let body: BodyInit | undefined
  if (options.body !== undefined) {
    headers['Content-Type'] = 'application/json'
    body = JSON.stringify(options.body)
  }

  const res = await fetch(url, { method: options.method ?? 'GET', headers, body })

  if (!res.ok) throw new ApiError(res.status, await errorBody(res))

  if (res.status === 204) return undefined as T
  return (await res.json()) as T
}

class HttpApiClient implements ApiClient {
  me() {
    return request<{ name: string; email: string | null }>('/me')
  }

  submitOrder(form: OrderForm) {
    return request<{ order: Order }>('/orders', {
      method: 'POST',
      body: form,
      idempotencyKey: form.submission_key,
    })
  }

  searchOrders(q: OrderSearch) {
    return request<OrderPage>(
      `/orders${query({ q: q.q, center: q.center, from: q.from, to: q.to, cursor: q.cursor })}`,
    )
  }

  getOrder(id: string) {
    return request<{ order: Order; email_to: string }>(`/orders/${encodeURIComponent(id)}`)
  }
}

let instance: ApiClient | null = null

function session(): Storage | null {
  try {
    return window.sessionStorage
  } catch {
    return null
  }
}

/**
 * Loads config, completes any redirect in flight, and signs in when there is no account - except
 * straight after a sign-out in this tab: then 'signed-out', and the caller shows the signed-out
 * screen. (pas-auth's /logout leaves the Microsoft session open, so signing in automatically
 * would put the same person straight back in.) The mark stays until the person clicks Sign in
 * (`signInAgain` clears it), so a reload keeps the signed-out screen; a signed-in user clears a
 * stale one.
 */
export async function bootstrap(): Promise<BootResult> {
  await loadConfig()
  await initAuth()
  const storage = session()
  if (activeAccount()) {
    if (storage) clearSignedOut(storage)
    return 'signed-in'
  }
  if (storage && isSignedOut(storage)) return 'signed-out'
  await signIn()
  return 'signed-in'
}

/** The single client instance the whole app shares. */
export function getClient(): ApiClient {
  if (!instance) instance = new HttpApiClient()
  return instance
}

/** Test seam: replace the implementation (the mock harness aliases this module wholesale). */
export function setClient(client: ApiClient | null): void {
  instance = client
}

export type { ApiClient, BootResult } from './contract'
